import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

const FADE_MS = 200;
const EDGE_PADDING = 24;
const MOVE_MIN_MS = 1200;
const MOVE_RANGE_MS = 1000;
const PAUSE_MS = 450;
const EASING = 'cubic-bezier(0.45, 0, 0.2, 1)';

export function useAgentWorkingLabel(agentName?: string | null) {
  const { t } = useTranslation();
  const name = agentName?.trim();
  return name
    ? t('AGENT_IS_WORKING', { defaultValue: '{{name}} is working…', name })
    : t('AGENT_WORKING', { defaultValue: 'Working…' });
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * "Agent is working" pointer for artifact canvases. Render inside a `relative` container;
 * it covers it without taking input. Positions are written straight to the DOM, so moving
 * never re-renders React.
 */
export function AgentWorkingCursor({ active, label }: { active: boolean; label: string }) {
  const [mounted, setMounted] = useState(active);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (active) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [active]);

  if (!mounted) return null;
  return <CursorLayer label={label} visible={visible} />;
}

function CursorLayer({ label, visible }: { label: string; visible: boolean }) {
  const layerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const pulseRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    const cursor = cursorRef.current;
    if (!layer || !cursor) return;
    const reduceMotion = prefersReducedMotion();
    let timer: number | undefined;

    const bounds = () => ({
      maxX: Math.max(EDGE_PADDING, layer.clientWidth - cursor.offsetWidth - EDGE_PADDING),
      maxY: Math.max(EDGE_PADDING, layer.clientHeight - cursor.offsetHeight - EDGE_PADDING),
    });
    const place = (x: number, y: number, durationMs: number) => {
      cursor.style.transitionDuration = `${durationMs}ms`;
      cursor.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    const placeAtCentre = () => {
      const { maxX, maxY } = bounds();
      place((EDGE_PADDING + maxX) / 2, (EDGE_PADDING + maxY) / 2, 0);
    };
    const pulse = () =>
      pulseRef.current?.animate?.(
        [
          { transform: 'scale(0.4)', opacity: 0.6 },
          { transform: 'scale(1.8)', opacity: 0 },
        ],
        { duration: 500, easing: 'ease-out' }
      );
    const glide = () => {
      const { maxX, maxY } = bounds();
      const duration = MOVE_MIN_MS + Math.random() * MOVE_RANGE_MS;
      place(
        EDGE_PADDING + Math.random() * (maxX - EDGE_PADDING),
        EDGE_PADDING + Math.random() * (maxY - EDGE_PADDING),
        duration
      );
      timer = window.setTimeout(() => {
        pulse();
        timer = window.setTimeout(glide, PAUSE_MS);
      }, duration);
    };

    placeAtCentre();
    // Keep the cursor inside the canvas when it resizes; the next glide picks new bounds.
    const resizeObserver = new ResizeObserver(() => {
      if (reduceMotion) placeAtCentre();
    });
    resizeObserver.observe(layer);
    if (!reduceMotion) timer = window.setTimeout(glide, PAUSE_MS);

    return () => {
      window.clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={layerRef}
      data-testid="agent-working-layer"
      className={cn(
        'pointer-events-none absolute inset-0 z-10 select-none overflow-hidden transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0'
      )}
    >
      <div
        ref={cursorRef}
        data-testid="agent-working-cursor"
        className="absolute left-0 top-0 flex items-start transition-transform will-change-transform"
        style={{ transitionTimingFunction: EASING }}
      >
        <span className="relative h-[18px] w-[18px] shrink-0">
          <span
            ref={pulseRef}
            aria-hidden="true"
            className="absolute -left-1.5 -top-1.5 h-4 w-4 rounded-full bg-primary/40 opacity-0"
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 18 18"
            className="relative h-[18px] w-[18px] text-primary drop-shadow-sm"
          >
            <path
              d="M2.5 1.5 15.5 7.9 9.4 9.5 6.6 15.6Z"
              fill="currentColor"
              stroke="white"
              strokeWidth="1.25"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span
          role="status"
          className="-ml-0.5 mt-3.5 whitespace-nowrap rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground shadow-md"
        >
          {label}
        </span>
      </div>
    </div>
  );
}
